import VideoOverlay from "@/components/Player/VideoOverlay/index";
import { useDeviceInfoQuery, useRouteChangeStatus } from "@/hooks";
import { streaksQualityLevels } from "@/lib/constants";
import dayjs from "@/lib/day";
import debugLog from "@/lib/debugLog";
import {
  MEDIA_ERROR_MESSAGES,
  PLAYER_ERROR_MESSAGES,
  StreaksPlayerError,
  WATCH_ERROR_MESSAGES,
} from "@/lib/errors";
import maybeIOS from "@/lib/maybeIOS";
import maybeIPad from "@/lib/maybeIPad";
import { liveStatus } from "@/lib/metaUtil";
import styles from "@/styles/Video.module.scss";
import classnames from "classnames";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import attachNPAWPlugin from "./NPAWPlugin";
import PlayerSurface from "./PlayerSurface";
import PlayerThumbnail from "./PlayerThumbnail";
import FullScreenOverlay from "./VideoOverlay/FullScreenOverlay";

function autoPlayEnabled(deviceInfo, disablePause) {
  debugLog({ maybeIOS: maybeIOS(deviceInfo) });
  debugLog({
    ua: deviceInfo?.ua,
    isIOS: deviceInfo?.isIOS,
    isIPad: deviceInfo?.isIPad,
    isSafari: deviceInfo?.isSafari,
    isMac: deviceInfo?.isMac,
  });
  // iOSではautoPlayしない
  if (maybeIOS(deviceInfo)) return false;
  // pauseイベントの中でplayをコールするという力技を行うとSafariのAuto-Play-Blockingに引っかかった時にエラーが無限ループするのでsafariで後追いライブだったらオートプレイをしない
  if (deviceInfo?.isSafari && disablePause) return false;
  return true;
}

function StreaksPlayer({
  playerOption = {},
  poster,
  watch,
  watchState,
  prevState,
  entitlements,
  onReady,
  user,
  isChatAvailableMeta,
  chatDisplay,
  setChatDisplay,
  isValidChatPeriod,
}) {
  const { isRouting } = useRouteChangeStatus();
  const { data: { deviceInfo } = {} } = useDeviceInfoQuery({
    fetchPolicy: "cache-first",
  });
  const router = useRouter();
  const playerRef = useRef();
  const overlayRef = useRef(null);
  const [playerError, setPlayerError] = useState();
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [playerInitFlag, setPlayerInitFlag] = useState(false);
  const now = dayjs();

  // 広告とPiPの出しわけ判定
  // iOS／iPadOS：PIPボタンを表示させる（広告再生をしない対応については、広告側でVASTを返さない対応をする前提のため、プレイヤー側では何もしない）
  // Android：アプリキックとなるため、今回実装対象外
  // PC（Win・macOS）：メタの広告有無によってPIPボタンを出し分ける
  //  PC（Win・macOS）：メタの広告あり→PIPなし、メタの広告なし→PIPあり
  // ※PCについては全画面表示となってもネイティブプレイヤーではないため、全画面表示でもPIPボタンを出し分けます
  const includeAd = useMemo(() => {
    // isIOS/iOS以外で広告を含む場合 true
    if (
      watchState &&
      watchState.mediaSource &&
      deviceInfo &&
      !maybeIOS(deviceInfo) &&
      !maybeIPad(deviceInfo) &&
      !deviceInfo.isIOS &&
      !deviceInfo.isIPad
    ) {
      return !isEmpty(watchState.mediaSource.ads);
    }
    // それ以外
    else {
      return false;
    }
  }, [watchState, deviceInfo]);

  // iOSで後追いなしのライブの場合はフルスクリーンボタンを表示しないようにしました。
  // https://backlog.smartstream.ne.jp/backlog/view/3RD_FE_PLAY_SMC-610#comment-240354
  const disableFullscreen = useMemo(() => {
    const isEPG = !!watch.episode.source_epg_id;
    if (
      maybeIOS(deviceInfo) &&
      liveStatus(watch.episode) &&
      (isEPG || !watch.episode.start_over_flag)
    ) {
      return true;
    } else {
      return false;
    }
  }, [deviceInfo, watch.episode]);

  // ライブ中の後追いなしライブは一時停止させない
  const disablePause = useMemo(() => {
    if (liveStatus(watch.episode) && !watch.episode.start_over_flag) {
      return true;
    } else {
      return false;
    }
  }, [watch.episode]);

  // 見逃しなしライブをライブ視聴で再生終了した場合は最初から再生ボタンを非表示にする
  // フラグ更新しない場合があったので依存配列にnowを追加する
  const disablePlayButtonOnLiveEnd = useMemo(() => {
    if (watch.episode.is_live && !watch?.episode?.catch_up_flag) {
      const now = dayjs();
      const encodeEnd = dayjs(watch.episode.encode_end_date);
      const liveEnded = now.isSameOrAfter(encodeEnd);
      return liveEnded;
    } else {
      return false;
    }
  }, [watch.episode, now]);

  // 後追いなしライブでSTREAKSプレイヤーで一時停止ボタンが表示されるため、
  // SPOOXでは後追いなしライブは再生中に一時停止させない仕様のため、案件側で非表示にする
  // 再生終了後はボタンの非表示にする処理を除外
  const hidePlayControlButtonForNonStartOverLive = useMemo(() => {
    if (watch.episode.is_live && !watch.episode.start_over_flag) {
      const now = dayjs();
      const encodeStart = dayjs(watch.episode.encode_start_date);
      const encodeEnd = dayjs(watch.episode.encode_end_date);
      const isOnAir = now.isBetween(encodeStart, encodeEnd, null, "[)");
      return isOnAir;
    } else {
      return false;
    }
  }, [watch.episode, now]);

  const autoPlay = autoPlayEnabled(deviceInfo, disablePause);

  const env = process.env.APP_ENV || process.env.NODE_ENV || "development";
  const debug = env !== "production";

  const handlePlayerReady = useCallback(() => {
    playerRef.current.on("error", () => {
      // player error handling
      const err = playerRef.current.error();
      const code = err.code;
      const npawMessage =
        MEDIA_ERROR_MESSAGES[code] ||
        PLAYER_ERROR_MESSAGES[code] ||
        err.message ||
        WATCH_ERROR_MESSAGES[0];
      // 連携されないNPAWエラーを送る処理追加
      window.plugin.fireError(err.code, npawMessage);
      setPlayerError(new StreaksPlayerError(err.message, err.code));
    });

    if (onReady) {
      onReady(playerRef.current);
    }
  }, []);

  const loadMedia = useCallback(() => {
    const { canplay, mediaSource, resumePoint } = watchState;
    if (!mediaLoaded && canplay && mediaSource && !!deviceInfo) {
      // test
      // const url =
      //   "https://multiplatform-f.akamaihd.net/i/multi/april11/sintel/sintel-hd_,512x288_450_b,640x360_700_b,768x432_1000_b,1024x576_1400_m,.mp4.csmil/master.m3u8"; // debug
      // player.src([{ src: url, type: "application/x-mpegURL" }]);

      const qualityLevels = (() => {
        const levels = streaksQualityLevels;

        // mediaSource.sourcesのresolutionをみて最大画質を決める
        const source = mediaSource.sources.find((source) => {
          if (deviceInfo?.isSafari) {
            return !!source.key_systems["com.apple.fps.1_0"];
          } else {
            return !source.key_systems["com.apple.fps.1_0"];
          }
        });
        const height = source.resolution.split("x")[1];
        const filterdLevels = levels.filter(
          (level) => level.maximumHeight <= height
        );
        return [...filterdLevels, { label: "自動", selected: true }];
      })();

      const loadMediaOption = {
        customQualityLevels: qualityLevels,
      };
      if (resumePoint !== null && resumePoint >= 0) {
        loadMediaOption.startTime = resumePoint;
      }
      const uid2Token = watch?.uid2Token?.token ?? "null";
      const uid2Params = encodeURIComponent(`uid2_token=${uid2Token}`);
      loadMediaOption.advertising = {
        adsParams: {
          cust_params: uid2Params,
        },
      };

      let newMediaSource;
      // iPadOS/iOSの場合はadsを[]で上書き
      if (maybeIOS(deviceInfo) || maybeIPad(deviceInfo) || deviceInfo.isIPad) {
        newMediaSource = { ...mediaSource, ads: [] };
      } else {
        newMediaSource = { ...mediaSource };
      }

      // ライブのシークバー用サムネイル（live_thumbnails.vtt のトラック）は、DASH/MSE 再生時に
      // 配信時間とともに肥大化し、メインスレッドの同期処理が longtask となってバッファ枯渇
      // →フリーズを招く（Chrome で発生、Mac で顕著）。DASH はサムネイルを manifest の
      // image track から取得できるため VTT は冗長だが、HLS/FairPlay（Safari 等のネイティブ
      // HLS）は image track を持たず VTT が唯一のサムネイル源のため除去してはならない。
      // よって「ライブ かつ 再生ソースが DASH（FairPlay 以外）」のときだけ thumbnail を除去する
      // （VOD は対象外）。判定は kind/label/src のいずれかに thumb を含むかで行う。
      const playbackSource = newMediaSource.sources?.find((source) =>
        deviceInfo?.isSafari
          ? !!source.key_systems?.["com.apple.fps.1_0"]
          : !source.key_systems?.["com.apple.fps.1_0"]
      );
      const isDashSource =
        !!playbackSource && !playbackSource.key_systems?.["com.apple.fps.1_0"];
      if (
        watch.episode?.is_live &&
        isDashSource &&
        Array.isArray(newMediaSource.tracks)
      ) {
        newMediaSource = {
          ...newMediaSource,
          tracks: newMediaSource.tracks.filter((track) => {
            const sig = `${track?.kind ?? ""} ${track?.label ?? ""} ${
              track?.src ?? ""
            }`.toLowerCase();
            return !sig.includes("thumb");
          }),
        };
      }

      playerRef.current.loadMedia(newMediaSource, loadMediaOption, () => {
        attachNPAWPlugin(playerRef.current, user, watch, debug);
      });
      playerRef.current.play();
      setMediaLoaded(true);
    }
  }, [watch, user, watchState, mediaLoaded, debug, deviceInfo]);

  const handlePlay = useCallback(() => {
    if (watchState.reason === "entitlement_reconfirm") {
      return watchState.displayOverlay({ reason: "entitlement_confirm" });
    }
    if (!mediaLoaded) {
      loadMedia();
    }
  }, [watchState, mediaLoaded]);

  useEffect(() => {
    const { mediaSource } = watchState;

    if (watch.episode && !playerRef.current && !!mediaSource && !!deviceInfo) {
      const video = document.createElement("video");
      video.setAttribute("poster", poster);
      video.setAttribute("controls", true);
      document.querySelector(`.${styles.videoWrapper}`).append(video);
      const options = {
        language: "ja",
        chromeless: false,
        timeShift: watch.episode.is_live && watch.episode.start_over_flag,
        pictureInPicture: true,
        // preload: "metadata",
        crossorigin: "anonymous",
        autoplay: autoPlay,
        disableInlineFullscreen: true,
      };

      if (includeAd) {
        options.advertising = {
          debug,
          requestMode: "ondemand",
          repeat: false,
        };
      }
      playerRef.current = streaksplayer(
        video,
        { ...options, ...playerOption },
        handlePlayerReady
      );
      setPlayerInitFlag(true);
    }
  }, [watch, autoPlay, debug, watchState, deviceInfo]);

  useEffect(() => {
    debugLog({ autoPlay });
    const { canplay, mediaSource } = watchState;
    if (canplay && mediaSource && autoPlay && playerInitFlag) {
      loadMedia();
    }
    // next_episode以外のダイアログが表示された場合に全画面を解除する？
  }, [watchState, autoPlay, playerInitFlag]);

  useEffect(() => {
    // PiP中にページ遷移するとPiPのウインドウが残ってしまうので要素が残っていたらexitPictureInPictureを実行する
    return () => {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture();
      }
    };
  }, []);

  // fullscreenchangeイベントの監視と状態の更新
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.on("fullscreenchange", () => {
        setIsFullScreen(playerRef.current.isFullscreen());
        // iPadでチャット入力欄にフォーカス合っているとすぐにフルスクリーンが解除されてしまうのでフォーカスを外す
        document.activeElement?.blur();
      });
    }
  }, [playerRef.current]);

  // playerの子要素としてFullscreenOverlayを追加
  useEffect(() => {
    if (overlayRef.current && playerRef.current) {
      playerRef.current.el().appendChild(overlayRef.current);
    }
    return () => {
      if (overlayRef.current && playerRef.current) {
        if (playerRef.current.el().contains(overlayRef.current)) {
          playerRef.current.el().removeChild(overlayRef.current);
        }
      }
    };
  }, [overlayRef.current, playerRef.current]);

  // fullscreenの状態に応じてFullscreenOverlayの表示・非表示を切り替える
  useEffect(() => {
    if (overlayRef.current) {
      if (isFullScreen) {
        overlayRef.current.style.visibility = "visible";
      } else {
        overlayRef.current.style.visibility = "hidden";
      }
    }
  }, [isFullScreen, overlayRef.current]);

  useEffect(() => {
    const handler = (url) => {
      setMediaLoaded(false);
      overlayRef.current = null;
      if (playerRef.current) {
        playerRef.current.pause();
        //プレイヤー以外の画面に遷移時にプレイヤー破棄
        if (url && !url.includes("/watch")) {
          playerRef.current.dispose();
        }
      }
    };
    router.events.on("routeChangeStart", handler);
    return () => {
      router.events.off("routeChangeStart", handler);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <PlayerThumbnail
          poster={poster}
          watch={watch}
          mediaLoaded={mediaLoaded}
          state={watchState}
        ></PlayerThumbnail>
        <PlayerSurface
          active={
            (watchState.canplay && !mediaLoaded && !isRouting) ||
            watchState.overlay
          }
          onClick={handlePlay}
          watch={watch}
        />
        {!isFullScreen && (
          <VideoOverlay
            watch={watch}
            state={watchState}
            entitlements={entitlements}
            error={playerError}
            poster={poster}
            isChatAvailableMeta={isChatAvailableMeta}
            setChatDisplay={setChatDisplay}
            isValidChatPeriod={isValidChatPeriod}
            chatDisplay={chatDisplay}
          />
        )}
        <div
          className={classnames(styles.videoWrapper, {
            [styles.disableFullscreen]: disableFullscreen,
            [styles.disablePause]: disablePause,
            [styles.disablePictureInPicture]: includeAd,
            [styles.hidePlayControlButtonForNonStartOverLive]:
              hidePlayControlButtonForNonStartOverLive,
            [styles.disablePlayButtonOnLiveEnd]: disablePlayButtonOnLiveEnd,
          })}
        />
        <div ref={overlayRef}>
          <FullScreenOverlay
            watch={watch}
            state={watchState}
            prevState={prevState}
            error={playerError}
            fullScreen={isFullScreen}
            player={playerRef.current}
          />
        </div>
      </div>
    </div>
  );
}

export default StreaksPlayer;
